import React, { useEffect, useState } from 'react';
import { Flowbite } from 'flowbite-react';
import customTheme from './theme';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { BridgeProvider } from './contexts/BridgeContext';
import { permissionManager } from './services/PermissionManager';
import LandingPage from './components/LandingPage';
import Dashboard from './components/Dashboard';
import Quran from './components/Quran';
import DeenHelpers from './components/dashboard/DeenHelpers';
import GoalPlannerPage from './pages/GoalPlannerPage';
import SimpleDashboard from './components/SimpleDashboard';
import Login from './components/Login';
import SignUp from './components/SignUp';
import ForgotPassword from './components/ForgotPassword';
import SettingsPage from './components/SettingsPage';
import Layout from './components/Layout';
import ClockLayout from './components/ClockLayout';
import AdhanClockPage from './pages/AdhanClockPage';
import './styles/smartMirror.css';
import DhikrHelper from './components/DhikrHelper';
import NamesOfAllah from './components/NamesOfAllah';
import GoalPlanner from './components/GoalPlanner';
import TVSettingsPage from './components/settings/TVSettingsPage';
import SubscriptionPage from './components/settings/SubscriptionPage';
import { DarkModeProvider } from './contexts/DarkModeContext';
import QiblaFinder from './components/QiblaFinder';
import PublicLayout from './components/PublicLayout';
import AboutPage from './pages/AboutPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import ContactPage from './pages/ContactPage';
import { cleanImageCache } from './utils/imageCache';
import CustomSplash from './components/CustomSplash';
import MobileGoalPlanner from './components/MobileGoalPlanner';
import AnalyticsPage from './components/analytics/AnalyticsPage';
import DuaExplorerPage from './pages/DuaExplorerPage';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AppTermsOfService from './components/app/AppTermsOfService';
import AppPrivacyPolicy from './components/app/AppPrivacyPolicy';
import AppForgotPassword from './components/app/AppForgotPassword';
import FeedbackForm from './components/FeedbackForm';
import FinalizePasswordReset from './components/FinalizePasswordReset';
import AuthActionHandler from './components/AuthActionHandler';
import ScrollToTop from './components/ScrollToTop';
import AppLanding from './components/app/AppLanding';
import { getDeviceId } from './utils/deviceIdentifier';
import SuperAdminRoute from './components/admin/SuperAdminRoute';
import AdminLayout from './components/admin/AdminLayout';
import OverviewPage from './components/admin/pages/OverviewPage';
import MosqueAdminManagement from './components/admin/pages/MosqueAdminManagement';
import UserManagement from './components/admin/pages/UserManagement';
import MosqueManagement from './components/admin/pages/MosqueManagement';
import SystemLogs from './components/admin/pages/SystemLogs';
import CreateMosque from './components/admin/pages/CreateMosque';
import EditMosque from './components/admin/pages/EditMosque';
import ManagePrayerTimes from './components/admin/pages/ManagePrayerTimes';

const PrivateRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }
  
  return currentUser ? children : <Navigate to="/login" replace />;
};

const PublicRoute = ({ children }) => {
  const { currentUser } = useAuth();
  return !currentUser ? children : <Navigate to="/adhan-clock" replace />;
};

// Root redirect: send authenticated users to dashboard, others to login
const RootRedirect = () => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return currentUser ? <Navigate to="/adhan-clock" replace /> : <Navigate to="/login" replace />;
};

function App() {
  const [isAppReady, setIsAppReady] = useState(false);

  useEffect(() => {
    // Initialize permission manager
    permissionManager.init();

    // Clean old cached images once per week
    cleanImageCache();
    const cleanupInterval = setInterval(cleanImageCache, 7 * 24 * 60 * 60 * 1000);
    
    // Send device ID to native app if running in GeckoView
    async function sendDeviceIdToNative() {
      if (window.browser && window.browser.runtime && window.browser.runtime.sendNativeMessage) {
        try {
          const deviceId = await getDeviceId();
          window.browser.runtime.sendNativeMessage("browser", { request: "setDeviceId", deviceId });
          console.log('[NativeAuth] Sent deviceId to native app:', deviceId);
        } catch (err) {
          console.error('[NativeAuth] Failed to send deviceId to native app:', err);
        }
      }
    }
    sendDeviceIdToNative();

    return () => {
      clearInterval(cleanupInterval);
      permissionManager.cleanup();
    };
  }, []);

  return (
    <DarkModeProvider>
      <CustomSplash onReady={() => setIsAppReady(true)} />
      <Flowbite theme={{ theme: customTheme }}>
        <Router>
          <AuthProvider>
            <SettingsProvider>
              <BridgeProvider>
                <ScrollToTop>
                  <Routes>
                    {/* Root redirect - landing page is now at ummahti.net (Vercel) */}
                    <Route path="/" element={<RootRedirect />} />
                    <Route path="/simpledashboard" element={<SimpleDashboard />} />

                    {/* Public pages only */}
                    <Route element={<PublicLayout />}>
                      <Route path="/old-landing" element={<LandingPage />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/signup" element={<SignUp />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/about" element={<AboutPage />} />
                      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                      <Route path="/privacy" element={<PrivacyPolicy />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/terms" element={<TermsOfService />} />
                      <Route path="/app/terms" element={<AppTermsOfService />} />
                      <Route path="/app/privacy" element={<AppPrivacyPolicy />} />
                      <Route path="/app/forgot-password" element={<AppForgotPassword />} />
                      <Route path="/finalize-password-reset" element={<FinalizePasswordReset />} />
                      <Route path="/__/auth/action" element={<AuthActionHandler />} />
                      <Route path="/app/landing" element={<AppLanding />} />
                    </Route>

                    {/* Adhan Clock management — focused layout */}
                    <Route element={<ClockLayout />}>
                      <Route path="/adhan-clock" element={<PrivateRoute><AdhanClockPage /></PrivateRoute>} />
                    </Route>

                    {/* Main app pages (accessible to all, with Layout) */}
                    <Route element={<Layout />}>
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/quran" element={<Quran />} />
                      <Route path="/deen-helpers" element={<DeenHelpers />} />
                      <Route path="/goal-planner" element={<GoalPlanner />} />
                      <Route path="/mobile-goals" element={<MobileGoalPlanner />} />
                      <Route path="/analytics" element={<AnalyticsPage />} />
                      <Route path="/dua-explorer" element={<DuaExplorerPage />} />
                      <Route path="/settings" element={<SettingsPage />} />
                      <Route path="/dhikr-helper" element={<DhikrHelper />} />
                      <Route path="/names-of-allah" element={<NamesOfAllah />} />
                      <Route path="/qibla-finder" element={<QiblaFinder />} />
                      <Route path="/feedback" element={<FeedbackForm />} />
                      {/* ACCOUNT-BASED FEATURES - still require login */}
                      <Route path="/settings/tv" element={<PrivateRoute><TVSettingsPage /></PrivateRoute>} />
                      <Route path="/settings/subscription" element={<PrivateRoute><SubscriptionPage /></PrivateRoute>} />
                      <Route path="/settings/subscription/success" element={<PrivateRoute><SubscriptionPage /></PrivateRoute>} />
                      <Route path="/settings/subscription/cancel" element={<PrivateRoute><SubscriptionPage /></PrivateRoute>} />
                    </Route>

                    {/* Super Admin Dashboard */}
                    <Route path="/admin" element={<SuperAdminRoute><AdminLayout /></SuperAdminRoute>}>
                      <Route path="overview" element={<OverviewPage />} />
                      <Route path="mosque-admins" element={<MosqueAdminManagement />} />
                      <Route path="users" element={<UserManagement />} />
                      <Route path="mosques" element={<MosqueManagement />} />
                      <Route path="mosques/create" element={<CreateMosque />} />
                      <Route path="mosques/:mosqueId/edit" element={<EditMosque />} />
                      <Route path="mosques/:mosqueId/prayer-times" element={<ManagePrayerTimes />} />
                      <Route path="system" element={<SystemLogs />} />
                      <Route index element={<Navigate to="/admin/overview" replace />} />
                    </Route>

                    {/* Catch all route */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </ScrollToTop>
              </BridgeProvider>
            </SettingsProvider>
          </AuthProvider>
        </Router>
      </Flowbite>
    </DarkModeProvider>
  );
}

export default App;
